import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setEditingTheme, upsertTheme } from '../../../../stores/actions/themes';
import Heading from '../../../general-components/v2/heading';
import SlidingMenu, { SlidingMenuOption } from '../../../general-components/sliding-menu';
import EditThemeColorTab from './edit-theme-color-tab';
import EditThemeFontTab from './fonts-tab';
import { GreenroomState } from '../../../../stores/types/types';
import classNames from 'classnames';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import Portal from '../../../general-components/portal';
import TextInput from '../../../general-components/text-input';
import { Theme } from '../../../../stores/types/themes';
import GenerateContentTitle from '../../generate-content-title';
import { RegenerateStaleContentForMeeting } from '../../../../connection/generate-content';

export enum EDIT_THEME_SUBTAB {
	COLORS = 'Colors',
	FONTS = 'Fonts',
}

const slidingMenuOptions: SlidingMenuOption[] = [
	{label: EDIT_THEME_SUBTAB.COLORS},
	{label: EDIT_THEME_SUBTAB.FONTS}
]

export default function EditThemeTab() {
	const dispatch = useDispatch();
	const isEditing = useSelector((s: GreenroomState) => s.ThemesReducer.themeEditing);
	const theme = useSelector((s: GreenroomState) => s.ThemesReducer.viewedTheme);
	const channel = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.channel);
	const meeting = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.meeting);
	const defaultThemes = useSelector((s: GreenroomState) => s.ThemesReducer.defaultThemes);

	
	const [tab, setTab] = useState(slidingMenuOptions[0]);
	const [unEditedTheme, setUnEditedTheme] = useState<Partial<Theme>>({colors:[],name:''});
	const [editedColors, setEditedColors] = useState<string[]>(theme?.colors ?? []);
	const [name, setName] = useState('');
	const [showNameModal, setShowNameModal] = useState(false);
	const [hasEdits, setHasEdits] = useState(false);

	const {currTemplate, applyThemeToItem} = useContext(FormContext);

	useEffect(() => {
		if (theme) {
			setUnEditedTheme({colors: [...theme.colors], name: theme.name});
			setEditedColors([...theme.colors]);
			setName(`${theme.name}`);
			setHasEdits(false);
		}
	}, [theme]);

	const saveForReal = useCallback(async () => {
		if (!channel) {
			console.error('Could not save theme, no channel');
			return;
		}
		setShowNameModal(false);
		const nTheme: Partial<Theme> = {
			...(theme ?? {}),
			name: name,
			colors: editedColors,
			stylepack: currTemplate.name
		};
		
		// for now, if the theme is a default theme, remove the id to force a new
		if (defaultThemes.some(x => x.id === nTheme.id)) {
			nTheme.id = 0;
		}

		// Create the action and await its promise
		const action = upsertTheme(channel, nTheme);
		dispatch(action);

		try {
			const savedTheme = await action.promise;
			if (savedTheme && meeting) {
				RegenerateStaleContentForMeeting(meeting, { themeIds: [savedTheme.id] });
			}
		} catch (error) {
			console.error('Failed to save theme:', error);
		}
	}, [channel, name, editedColors, currTemplate.name, theme]);

	const onSave = useCallback(() => {
		if (isEditing) {
			saveForReal();
		} else {
			if (theme?.name === name) {
				// if creating a theme and they have not changed the name,
				// set to blank so modal will prompt for name
				setName('');
			}
			setShowNameModal(true);
		}
	}, [saveForReal, isEditing, name, theme?.name]);

	const editColorSwatch = useCallback((color: string, index: number) => {
		if (!editedColors?.length) return;
		editedColors[index] = color.toUpperCase();
		setEditedColors(editedColors);
		setHasEdits(true);
		applyThemeToItem({...theme, colors: editedColors});
	}, [editedColors, theme?.colors, unEditedTheme?.colors]);

	const backDropClicked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		setShowNameModal(false);
		e.stopPropagation();
	}
	
	const onCancelEdit = useCallback(() => {
		applyThemeToItem(unEditedTheme);
		dispatch(setEditingTheme(null, false));
	}, [unEditedTheme, applyThemeToItem]);

	return (<div className={classNames('editing-theme-swatches-tab', {open: !!theme})}>
		<div className='edit-theme-tab'>
			<Heading
				backButtonFunction={() => dispatch(setEditingTheme(null, false))}
				showXButton={false}
				title={<GenerateContentTitle
					key={theme?.id}
					title={name ?? theme?.name}
					placeholder='My new theme'
					allowBlank
					saveCallback={(newName: string) => {
						if (name !== newName) {
							setHasEdits(true);
							setName(newName);
						}
					}}
				/>}
			/>
			<SlidingMenu 
				options={slidingMenuOptions}
				selectedOption={tab}
				callback={setTab}
				className='position-relative width-calc-full'
				fullWidthDivider={true}
				dividerClassname={'lemonade'}
			/>
			<h6 className='bl-font-header edit-theme-tab-header'>In this Stylepack</h6>
			{tab.label === EDIT_THEME_SUBTAB.COLORS 
				? <EditThemeColorTab onSave={onSave} onCancel={onCancelEdit} editColor={editColorSwatch} editedColors={editedColors} hasEdits={hasEdits}/>
				: <EditThemeFontTab theme={theme} />}
			{showNameModal ? <Portal targetId='right-click-portal'>
				<div className='name-theme-modal-backdrop fade-in-fast' onClick={backDropClicked}/>
				<div className='name-theme-modal fade-in-fast' onClick={(e) => e.stopPropagation()}>
					<h4 className='bl-font-header'>Give your theme a name</h4>
					<TextInput placeholder={'My new theme'} value={name ?? ''} onChange={(e) => setName(e.target.value)} required={true}/>
					<div className='flex-center'>
						<button className='button-pillbox dullgray cancel-theme-button' onClick={() => setShowNameModal(false)}>Cancel</button>
						<button className='button-pillbox sherbert' onClick={saveForReal} disabled={name === ''}>Save</button>
					</div>
				</div>
			</Portal> : null}
		</div>
	</div>);
}
